import { Button, type ButtonProps } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useAuth } from '@/lib/auth'
import { hasFormAdminAccess } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { useFormBuilderStore } from '@/stores/formBuilderStore'
import { FolderPlus, Plus, Settings2 } from 'lucide-react'
import { forwardRef, useState } from 'react'
import { AddQuestionGroupDialog } from './AddQuestionGroupDialog'
import { CompletionPageSettings } from './CompletionPageSettings'
import { QuestionOutlineList } from './QuestionOutlineList'
import { QuestionTypeSelector } from './QuestionTypeSelector'

export function QuestionList() {
  const { user } = useAuth()
  const canManage = hasFormAdminAccess(user)
  const questions = useFormBuilderStore((state) => state.questions)
  const questionGroups = useFormBuilderStore((state) => state.questionGroups)
  const addQuestion = useFormBuilderStore((state) => state.addQuestion)
  const [groupDialogOpen, setGroupDialogOpen] = useState(false)

  return (
    <aside className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden border-r border-slate-200 bg-white">
      <div className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4">
        <div className="min-w-0">
          <div className="font-semibold">問題</div>
          <div className="text-xs text-slate-500">
            {questions.length} 題 · {questionGroups.length} 題組
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <QuestionTypeSelector
            onSelect={(type) => addQuestion(type)}
            trigger={
              <HeaderIconButton label="新增問題" disabled={!canManage}>
                <Plus className="size-4" />
              </HeaderIconButton>
            }
          />
          <HeaderIconButton
            label="新增題組"
            disabled={!canManage}
            onClick={() => setGroupDialogOpen(true)}
          >
            <FolderPlus className="size-4" />
          </HeaderIconButton>
          <CompletionPageSettings
            trigger={
              <HeaderIconButton label="問卷設定">
                <Settings2 className="size-4" />
              </HeaderIconButton>
            }
          />
        </div>
      </div>
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-3">
        {!canManage && (
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
            目前為非管理員檢視，題目與題組只可預覽，不可新增、排序或刪除。
          </div>
        )}
        <QuestionOutlineList />
      </ScrollArea>
      <AddQuestionGroupDialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen} />
    </aside>
  )
}

const HeaderIconButton = forwardRef<HTMLButtonElement, ButtonProps & { label: string }>(
  ({ label, children, className, ...props }, ref) => (
    <Button
      ref={ref}
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      title={label}
      className={cn('group relative', className)}
      {...props}
    >
      {children}
      <span className="pointer-events-none absolute top-full left-1/2 z-50 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md border border-slate-200 bg-slate-950 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
        {label}
      </span>
    </Button>
  ),
)
HeaderIconButton.displayName = 'HeaderIconButton'
